import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Edit2,
  Copy,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Plus,
  Search,
  FileText,
  // Play,
  Clock,
  BarChart2,
  Sliders,
} from "react-feather";

import BaseButton from "../../../../BaseComponents/BaseButton";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";
import BaseModal from "../../../../BaseComponents/BaseModal";
import BaseInput from "../../../../BaseComponents/BaseInput";
import PayrollStatusBadge from "../../../../BaseComponents/PayrollStatusBadge";
import {
  listTemplatesApi,
  cloneTemplateApi,
  toggleTemplateApi,
  deleteTemplateApi,
} from "../../../../Api/payrollApi";
import { checkStatusCodeSuccess, rendorBackButton } from "../../../../Constant/common";
import { PAYROLL_STRINGS } from "../../../../Constant/HR/payrollConstants";
import "./PayrollTemplateBuilder.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const countComponents = (template) => {
  const earnings = Array.isArray(template?.earningComponents)
    ? template.earningComponents.length
    : 0;
  const deductions = Array.isArray(template?.deductionComponents)
    ? template.deductionComponents.length
    : 0;
  return { earnings, deductions };
};

// ---------------------------------------------------------------------------
// Action icon button
// ---------------------------------------------------------------------------

const ActionBtn = ({ onClick, icon: Icon, color, bg, tooltip, tooltipColor, ariaLabel }) => (
  <BaseTooltip label={tooltip} color={tooltipColor}>
    <button
      type="button"
      aria-label={ariaLabel || tooltip}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: 8,
        border: "none",
        background: bg,
        color: color,
        cursor: "pointer",
        transition: "opacity 0.15s, transform 0.15s",
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = "0.8";
        e.currentTarget.style.transform = "scale(1.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = "1";
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <Icon size={15} strokeWidth={2.2} />
    </button>
  </BaseTooltip>
);

// ---------------------------------------------------------------------------
// Template card
// ---------------------------------------------------------------------------

const TemplateCard = ({ template, onEdit, onClone, onToggle, onDelete, style }) => {
  const isActive = template?.status === "Active";
  const accentColor = isActive ? "#10b981" : "#f59e0b";
  const { earnings, deductions } = countComponents(template);
  const rolesCount = Array.isArray(template?.roles) ? template.roles.length : 0;

  return (
    <div
      className="glass rounded-3 animate-slide-up d-flex flex-column"
      style={{
        overflow: "hidden",
        transition: "box-shadow 0.25s ease, transform 0.25s ease",
        cursor: "default",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 24px 60px rgba(99,102,241,0.25)";
        e.currentTarget.style.transform = "translateY(-4px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Coloured top accent bar */}
      <div aria-hidden="true" style={{ height: 4, background: accentColor, flexShrink: 0 }} />

      {/* Card body */}
      <div className="p-4 d-flex flex-column gap-2 flex-grow-1">
        {/* Name + status badge */}
        <div className="d-flex align-items-start justify-content-between gap-2">
          <span
            className="fw-700 fs-6"
            style={{
              color: "var(--text-primary)",
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {template?.name || "Untitled Template"}
          </span>
          <PayrollStatusBadge status={template?.status || "Inactive"} size="sm" />
        </div>

        {/* Description */}
        {template?.description ? (
          <p
            className="fs-7 m-0"
            style={{
              color: "var(--text-secondary)",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {template.description}
          </p>
        ) : null}

        {/* Roles count pill */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span
            className="d-inline-flex align-items-center rounded-pill fs-8 fw-600"
            style={{
              background: "rgba(99,102,241,0.12)",
              color: "#6366f1",
              padding: "2px 10px",
              whiteSpace: "nowrap",
            }}
          >
            {rolesCount} {rolesCount === 1 ? "role" : "roles"}
          </span>

          {/* Component summary */}
          <span
            className="fs-8"
            style={{
              color: "var(--text-secondary)",
              whiteSpace: "nowrap",
            }}
          >
            {earnings} earnings · {deductions} deductions
          </span>
        </div>

        {/* Last modified */}
        <p
          className="m-0 fs-8"
          style={{
            color: "var(--text-secondary)",
          }}
        >
          Last modified: {formatDate(template?.updatedAt || template?.updated_at)}
        </p>
      </div>

      {/* Action buttons row */}
      <div
        className="d-flex align-items-center gap-1 p-3 border-top"
        style={{
          borderColor: "var(--dropdown-border)",
        }}
      >
        <ActionBtn
          onClick={() => onEdit(template)}
          icon={Edit2}
          color="#f59e0b"
          bg="rgba(245,158,11,0.1)"
          tooltip={PAYROLL_STRINGS.BTN_EDIT}
          tooltipColor="#b45309"
          ariaLabel={`Edit ${template?.name}`}
        />
        <ActionBtn
          onClick={() => onClone(template)}
          icon={Copy}
          color="#6366f1"
          bg="rgba(99,102,241,0.1)"
          tooltip={PAYROLL_STRINGS.BTN_CLONE}
          tooltipColor="#4f46e5"
          ariaLabel={`Clone ${template?.name}`}
        />
        <ActionBtn
          onClick={() => onToggle(template)}
          icon={isActive ? ToggleRight : ToggleLeft}
          color={isActive ? "#f59e0b" : "#10b981"}
          bg={isActive ? "rgba(245,158,11,0.1)" : "rgba(16,185,129,0.1)"}
          tooltip={isActive ? PAYROLL_STRINGS.BTN_DEACTIVATE : PAYROLL_STRINGS.BTN_ACTIVATE}
          tooltipColor={isActive ? "#b45309" : "#047857"}
          ariaLabel={`${isActive ? "Deactivate" : "Activate"} ${template?.name}`}
        />
        <ActionBtn
          onClick={() => onDelete(template)}
          icon={Trash2}
          color="#f43f5e"
          bg="rgba(244,63,94,0.1)"
          tooltip={PAYROLL_STRINGS.BTN_DELETE}
          tooltipColor="#be123c"
          ariaLabel={`Delete ${template?.name}`}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const PayrollTemplates = () => {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [toggleId, setToggleId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Force padding-left on search input after mount — bypasses global !important CSS
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.style.setProperty("padding-left", "36px", "important");
    }
  }, []);

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------

  const fetchTemplates = useCallback(() => {
    setIsLoading(true);
    listTemplatesApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setTemplates(Array.isArray(resp?.data) ? resp.data : []);
        } else {
          setTemplates([]);
        }
      })
      .catch(() => {
        setTemplates([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchTemplates(); // eslint-disable-line react-hooks/set-state-in-effect
  }, [fetchTemplates]);

  // -------------------------------------------------------------------------
  // Filtered list
  // -------------------------------------------------------------------------

  const filtered = templates.filter((t) =>
    (t?.name || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const handleEdit = (template) => {
    navigate(`/payrollTemplates/${template.id}/edit`);
  };

  const handleClone = (template) => {
    setActionLoading(true);
    cloneTemplateApi(template.id)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(PAYROLL_STRINGS.SUCCESS_TEMPLATE_CLONED);
          fetchTemplates();
        } else {
          toast.error(PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch(() => toast.error(PAYROLL_STRINGS.ERROR_GENERIC))
      .finally(() => setActionLoading(false));
  };

  const handleToggleRequest = (template) => {
    setToggleId(template.id);
  };

  const handleToggleConfirm = () => {
    if (!toggleId) return;
    setActionLoading(true);
    toggleTemplateApi(toggleId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const wasActive = templates.find((t) => t.id === toggleId)?.status === "Active";
          toast.success(
            wasActive
              ? PAYROLL_STRINGS.SUCCESS_TEMPLATE_DEACTIVATED
              : PAYROLL_STRINGS.SUCCESS_TEMPLATE_ACTIVATED,
          );
          fetchTemplates();
        } else {
          toast.error(PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch(() => toast.error(PAYROLL_STRINGS.ERROR_GENERIC))
      .finally(() => {
        setActionLoading(false);
        setToggleId(null);
      });
  };

  const handleDeleteRequest = (template) => {
    setDeleteId(template.id);
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    setActionLoading(true);
    deleteTemplateApi(deleteId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(PAYROLL_STRINGS.SUCCESS_TEMPLATE_DELETED);
          fetchTemplates();
        } else {
          toast.error(PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch(() => toast.error(PAYROLL_STRINGS.ERROR_GENERIC))
      .finally(() => {
        setActionLoading(false);
        setDeleteId(null);
      });
  };

  // -------------------------------------------------------------------------
  // Toggle modal meta
  // -------------------------------------------------------------------------

  const toggleTarget = templates.find((t) => t.id === toggleId);
  const isToggleActive = toggleTarget?.status === "Active";

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="animate-fade-in d-flex flex-column gap-3">
      {/* Page header */}
      <div className="animate-slide-down d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="page-header-title">{PAYROLL_STRINGS.TEMPLATES_TITLE}</h1>
          <p className="page-header-subtitle">{PAYROLL_STRINGS.TEMPLATES_SUBTITLE}</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <BaseButton
            color="primary"
            onClick={() => navigate("/payrollTemplates/new")}
            aria-label="Create new payroll template"
          >
            <Plus size={15} strokeWidth={2.5} aria-hidden="true" />
            {PAYROLL_STRINGS.BTN_NEW_TEMPLATE}
          </BaseButton>
          {rendorBackButton(() => navigate("/setting"))}
        </div>
      </div>

      {/* Quick navigation — links to all payroll sections */}
      <div className="payroll-nav-grid stagger" style={{ display: "grid", gap: 12 }}>
        <style>{`
          .payroll-nav-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
          @media (min-width: 640px)  { .payroll-nav-grid { grid-template-columns: repeat(3, 1fr); } }
          @media (min-width: 1024px) { .payroll-nav-grid { grid-template-columns: repeat(3, 1fr); } }
        `}</style>
        {[
          /*
          {
            icon: Play,
            color: "#6366f1",
            bg: "rgba(99,102,241,0.12)",
            label: "Run Payroll",
            desc: "Process monthly salaries",
            path: "/payrollRun",
          },
          */
          {
            icon: Clock,
            color: "#10b981",
            bg: "rgba(16,185,129,0.12)",
            label: "History",
            desc: "View past payroll batches",
            path: "/payrollHistory",
          },
          {
            icon: BarChart2,
            color: "#8b5cf6",
            bg: "rgba(139,92,246,0.12)",
            label: "Reports",
            desc: "Salary, PF, TDS, Bank transfer",
            path: "/payrollReports",
          },
          {
            icon: Sliders,
            color: "#f59e0b",
            bg: "rgba(245,158,11,0.12)",
            label: "PT Slabs",
            desc: "Professional Tax configuration",
            path: "/payrollSettings/ptSlabs",
          },
          {
            icon: Sliders,
            color: "#f43f5e",
            bg: "rgba(244,63,94,0.12)",
            label: "TDS Slabs",
            desc: "Income Tax slab configuration",
            path: "/payrollSettings/tdsSlabs",
          },
        ].map(({ icon: Icon, color, bg, label, desc, path }, i) => (
          <button
            key={path}
            type="button"
            onClick={() => navigate(path)}
            className="glass rounded-2xl animate-slide-up"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              border: "1px solid var(--glass-border)",
              cursor: "pointer",
              textAlign: "left",
              transition: "box-shadow 0.2s, transform 0.2s",
              animationDelay: `${i * 50}ms`,
              background: "transparent",
            }}
            aria-label={`Go to ${label}`}
          >
            <div
              className="rounded-2"
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon size={18} style={{ color }} strokeWidth={2} aria-hidden="true" />
            </div>
            <div style={{ minWidth: 0 }}>
              <p
                className="m-0"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </p>
              <p
                className="m-0"
                style={{
                  fontSize: "0.72rem",
                  marginTop: "1px",
                  color: "var(--text-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {desc}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Search bar */}
      <div
        className="glass rounded-3 animate-slide-up p-4"
        style={{ maxWidth: "100%", width: "100%" }}
      >
        <div style={{ width: 240 }}>
          <BaseInput
            ref={searchInputRef}
            name="searchQuery"
            type="search"
            value={searchQuery}
            handleChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates…"
            icon={<Search size={16} />}
            className="mb-0"
          />
        </div>
      </div>

      {/* Content area */}
      {isLoading ? (
        /* Loading state */
        <div className="glass rounded-3 animate-fade-in p-5 d-flex flex-column align-items-center gap-3">
          <span className="ds-spinner" role="status" aria-label="Loading templates" />
          <p className="m-0" style={{ color: "var(--text-secondary)", fontSize: "0.875rem" }}>
            Loading templates…
          </p>
        </div>
      ) : filtered.length === 0 ? (
        /* Empty state */
        <div className="glass rounded-3 animate-fade-in p-5 d-flex flex-column align-items-center gap-3 text-center">
          <FileText
            size={40}
            aria-hidden="true"
            style={{ color: "var(--text-secondary)", opacity: 0.5 }}
          />
          <p
            className="m-0"
            style={{
              color: "var(--text-secondary)",
              fontSize: "0.9375rem",
              maxWidth: 360,
            }}
          >
            {searchQuery ? `No templates match "${searchQuery}"` : PAYROLL_STRINGS.EMPTY_TEMPLATES}
          </p>
          {!searchQuery && (
            <BaseButton color="primary" onClick={() => navigate("/payrollTemplates/new")}>
              <Plus size={15} strokeWidth={2.5} aria-hidden="true" />
              {PAYROLL_STRINGS.BTN_NEW_TEMPLATE}
            </BaseButton>
          )}
        </div>
      ) : (
        /* Card grid */
        <>
          <div className="payroll-template-grid stagger">
            {filtered.map((template, idx) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleEdit}
                onClone={handleClone}
                onToggle={handleToggleRequest}
                onDelete={handleDeleteRequest}
                style={{ animationDelay: `${idx * 60}ms` }}
              />
            ))}
          </div>
        </>
      )}

      {/* Toggle confirm modal */}
      <BaseModal
        isOpen={!!toggleId}
        toggler={() => setToggleId(null)}
        title={isToggleActive ? PAYROLL_STRINGS.BTN_DEACTIVATE : PAYROLL_STRINGS.BTN_ACTIVATE}
        submit={handleToggleConfirm}
        submitText={isToggleActive ? PAYROLL_STRINGS.BTN_DEACTIVATE : PAYROLL_STRINGS.BTN_ACTIVATE}
        closeText={PAYROLL_STRINGS.BTN_CANCEL}
        loader={actionLoading}
        disabled={actionLoading}
        size="sm"
      >
        <p style={{ color: "var(--text-primary)", margin: 0, fontSize: "0.9rem", lineHeight: 1.6 }}>
          {isToggleActive
            ? PAYROLL_STRINGS.CONFIRM_DEACTIVATE_TEMPLATE
            : `Activate template "${toggleTarget?.name}"? It will be available for new payroll runs.`}
        </p>
      </BaseModal>

      {/* Delete confirm modal */}
      <BaseModal
        isOpen={!!deleteId}
        toggler={() => setDeleteId(null)}
        title={PAYROLL_STRINGS.BTN_DELETE}
        submit={handleDeleteConfirm}
        submitText={PAYROLL_STRINGS.BTN_DELETE}
        closeText={PAYROLL_STRINGS.BTN_CANCEL}
        loader={actionLoading}
        disabled={actionLoading}
        size="sm"
      >
        <p style={{ color: "#f43f5e", margin: 0, fontSize: "0.9rem", lineHeight: 1.6 }}>
          {PAYROLL_STRINGS.CONFIRM_DELETE_TEMPLATE}
        </p>
      </BaseModal>
    </div>
  );
};

export default PayrollTemplates;
